/**
 * Quran Assistant - Main App Component
 * 
 * Main application component for Quran Assistant PWA
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from '@/contexts/AuthContext';
import { useAuth } from '@/hooks/useAuth';
import LoginPage from '@/components/auth/LoginPage';
import RegisterPage from '@/components/auth/RegisterPage';
import AuthCallback from '@/components/auth/AuthCallback';
import AppLayout from '@/components/layout/AppLayout';
import { PageTransition } from '@/components/layout/PageTransition';
import HomePage from '@/components/home/HomePage';
import ReadPage from '@/components/read/ReadPage';
import ProfilePage from '@/components/profile/ProfilePage';
import NotificationPage from '@/components/notifications/NotificationPage';
import SetGoalForm from '@/components/goals/SetGoalForm';
import { RequireGoal } from '@/components/goals/RequireGoal';
import { GoalCompletionStory } from '@/components/goals/GoalCompletionStory';
import GoalHistoryPage from '@/components/goals/GoalHistoryPage';
import { ThemeProvider } from '@/components/ui/theme-provider';
import { LoadingScreen } from '@/components/ui/loading-screen';
import OnboardingPage from '@/components/onboarding/OnboardingPage';
import ShareAyahPage from '@/components/share/OneDayOneAyahSharePage';
import HijriCalendarPage from '@/components/hijri/HijriCalendarPage';
import FeedbackPage from '@/components/feedback/FeedbackPage';

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Public Route wrapper (redirect to dashboard if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// Root redirect - check onboarding status
function OnboardingOrDashboard() {
  const hasSeenOnboarding = localStorage.getItem('quran-assistant-onboarding-completed');
  
  if (!hasSeenOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }
  
  return <Navigate to="/dashboard" replace />;
}

function AppRoutes() {
  return (
    <PageTransition>
    <Routes>
      {/* Onboarding route */}
      <Route path="/onboarding" element={<OnboardingPage />} />

      {/* Auth routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      <Route
        path="/auth/callback"
        element={<AuthCallback />}
      />

      {/* Set goal (no layout) – แสดงเมื่อ login ครั้งแรกและยังไม่มี goal */}
      <Route
        path="/set-goal"
        element={
          <ProtectedRoute>
            <SetGoalForm />
          </ProtectedRoute>
        }
      />

      {/* Goal completion story (no layout) – หลังจบเป้าหมายหรือ wrap up */}
      <Route
        path="/goal-completed/:goalId"
        element={
          <ProtectedRoute>
            <GoalCompletionStory />
          </ProtectedRoute>
        }
      />

      {/* Goal history – ดูเป้าหมายที่ผ่านมา */}
      <Route
        path="/goal-history"
        element={
          <ProtectedRoute>
            <AppLayout>
              <GoalHistoryPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Protected routes with AppLayout – ต้องมี goal ก่อน */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <RequireGoal>
              <AppLayout>
                <HomePage />
              </AppLayout>
            </RequireGoal>
          </ProtectedRoute>
        }
      />
      <Route
        path="/read"
        element={
          <ProtectedRoute>
            <RequireGoal>
              <AppLayout>
                <ReadPage />
              </AppLayout>
            </RequireGoal>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <RequireGoal>
              <AppLayout>
                <ProfilePage />
              </AppLayout>
            </RequireGoal>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <RequireGoal>
              <AppLayout>
                <NotificationPage />
              </AppLayout>
            </RequireGoal>
          </ProtectedRoute>
        }
      />

      {/* Feedback (satisfaction survey) – protected, no RequireGoal */}
      <Route
        path="/feedback"
        element={
          <ProtectedRoute>
            <AppLayout>
              <FeedbackPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Standalone pages (no BottomNavigation) */}
      <Route
        path="/share/ayah/:surah/:ayah"
        element={
          <ProtectedRoute>
            <ShareAyahPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/hijri-calendar"
        element={
          <ProtectedRoute>
            <HijriCalendarPage />
          </ProtectedRoute>
        }
      />

      {/* Default redirect – ตรวจสอบ onboarding ก่อน */}
      <Route path="/" element={<OnboardingOrDashboard />} />
    </Routes>
    </PageTransition>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 60 * 1000 },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider defaultTheme="system" storageKey="vite-ui-theme">
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </ThemeProvider>
      </AuthProvider>
      <Toaster
        position="bottom-center"
        toastOptions={{
          duration: 3000,
          style: { width: '100%', maxWidth: '100%' },
        }}
        containerStyle={{
          bottom: '6rem'
        }}
      />
    </QueryClientProvider>
  );
}

export default App;
